import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import { MainLayout } from './components/layout/MainLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { Dashboard } from './pages/dashboard/Dashboard';
import { Assessments } from './pages/Assessments';
import { TakeAssessment } from './pages/assessments/TakeAssessment';
import { ContactUs } from './pages/ContactUs';
import { CodePractice } from './pages/CodePractice';
import { InterviewPrep } from './pages/InterviewPrep';
import NotFound from "./pages/NotFound";
import Index from "./pages/Index";
import { Profile } from './pages/profile/Profile';
import { ChangePassword } from './pages/profile/ChangePassword';
import { Courses } from './pages/courses/Courses';
import { CourseDetail } from './pages/courses/CourseDetail';
import { getAuth } from "firebase/auth";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { app } from "./firebase";
import { ThemeProvider } from "./components/theme-provider";
import { LoadingProvider } from './hooks/useLoading';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './index.css';
import { useToast } from './hooks/use-toast';

function App() {
  const { toast } = useToast();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;

      // Allow access if route is '/' or empty  
      if (location.pathname === '/' || location.pathname === '') {
        setIsLoading(false); // Skip authentication check  
        return; // No need to navigate anywhere  
      }

      if (userData && userData?.uid) {
        try {
          const uid = userData?.uid;
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where("uid", "==", uid));
          const querySnapshot = await getDocs(q);

          const studentQuery = query(
            collection(db, "students"),
            where("emailId", "==", userData.emailId)
          );
          const studentSnapshot = await getDocs(studentQuery);

          if (!studentSnapshot.empty) {
            const studentDoc = studentSnapshot.docs[0];
            const studentDetails: any = { id: studentDoc.id, ...studentDoc.data() };
            if(!studentDetails?.isLMSDone){
             
              localStorage.clear();
              window.location.href="/";
              setTimeout(()=>{
                toast({
                  title: "Access Denied",
                  description: "You Access Has Been Revoked , Please contact Reception",
                  variant: "destructive",
                });
              },2000)
            }
          }
          if (!querySnapshot.empty) {
            setIsLoggedIn(true);
            setIsLoading(false);
            navigate(location.pathname);
          } else {
            setIsLoading(false);
            console.error("No user record found in Firestore");
            navigate("/login");
          }
        } catch (error) {
          setIsLoading(false);
          console.error("Error during authentication:", error);
          navigate("/login");
        }
      } else {
        setIsLoading(false);
        console.error("No user data found in local storage");
        navigate("/login");
      }
    }

    checkUserLoggedIn();
  }, [navigate, auth, db, location]); // Include location in the dependencies 


  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <LoadingProvider>
        <Routes>
          <Route path="/" element={<Index />} /> {/* Index page does not require authentication. */}

          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>

          <Route element={<MainLayout />}>
            {/* Private routes that require authentication */}
            <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} isLoggedIn={isLoggedIn} />} />
            <Route path="/courses" element={<PrivateRoute element={<Courses />} isLoggedIn={isLoggedIn} />} />
            <Route path="/courses/:id" element={<PrivateRoute element={<CourseDetail />} isLoggedIn={isLoggedIn} />} />
            <Route path="/assessments" element={<PrivateRoute element={<Assessments />} isLoggedIn={isLoggedIn} />} />
            <Route path="/assessments/take/:id" element={<PrivateRoute element={<TakeAssessment />} isLoggedIn={isLoggedIn} />} />
            <Route path="/interview-prep" element={<PrivateRoute element={<InterviewPrep />} isLoggedIn={isLoggedIn} />} />
            <Route path="/code-practice" element={<PrivateRoute element={<CodePractice />} isLoggedIn={isLoggedIn} />} />
            <Route path="/contact" element={<PrivateRoute element={<ContactUs />} isLoggedIn={isLoggedIn} />} />
            <Route path="/profile" element={<PrivateRoute element={<Profile />} isLoggedIn={isLoggedIn} />} />
            <Route path="/profile/change-password" element={<PrivateRoute element={<ChangePassword />} isLoggedIn={isLoggedIn} />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster />
        <ToastContainer />
      </LoadingProvider>
    </ThemeProvider>
  );
}

// Revised PrivateRoute component to allow access to the Index page without authentication  
const PrivateRoute = ({ element, isLoggedIn }) => {
  return isLoggedIn || element.type === Index ? element : <Navigate to="/login" />;
};

export default App;  